import React, { useState, useEffect } from "react";
import { message, Form } from "antd";
import { AntdButton, CheckboxField } from "../../../blueplanitUtils/InputField";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setCheckedMailsData,
  setSelectedMails,
  setSelectedMailsData,
} from "../../../Slices/employeeslice";

import { setLoginUserDetails } from "../../../Slices/loginUserSlice";
import { updateEmpEmailSettingsThunk } from "../../../Slices/salesEmailsSlice";

import { updateEmpSharedEmailSettings } from "../../../Slices/sharedEmailSlice";

const EmployeeEmailSettings = () => {
  const dispatch = useDispatch();
  const { Empid } = useParams();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

  // Normal emails
  const emails = useSelector((state) => state.salesEmails.emails);
  const selectedEmails = useSelector((state) => state.employee.selectedEmails);
  const [checkedEmails, setCheckedEmails] = useState({});

  // Shared emails
  const { sharedemails, loading, error } = useSelector((state) => state.sharedEmail);
  const usersharedemails = useSelector((state) => state.sharedEmail.usersharedemails);
  const [checkedSharedEmails, setCheckedSharedEmails] = useState({});

  // --------------------- PRIVATE EMAIL LOGIC ---------------------
  useEffect(() => {
    if (emails) {
      const checks = {};
      const activeEmails = emails.filter((emailObj) => emailObj.status === true || emailObj.status === "true");

      activeEmails.forEach((emailObj) => {
        const selectedMatch = selectedEmails.find((sel) => sel.email === emailObj.email);
        const isChecked = selectedMatch?.status === true || selectedMatch?.status === "true";
        checks[emailObj.email] = isChecked;
      });

      setCheckedEmails(checks);
    }
  }, [emails, selectedEmails]);

  const toggleEmailCheckbox = (email) => {
    setCheckedEmails((prev) => ({
      ...prev,
      [email]: !prev[email],
    }));
  };

  const handleSubmitEmails = async () => {
    const selectedEmailsPayload = Object.entries(checkedEmails).map(([email, checked]) => ({
      email,
      status: Boolean(checked),
    }));

    const anySelected = selectedEmailsPayload.some((emailObj) => emailObj.status);
    if (!anySelected) return message.warning("Please select at least one email.");

    try {
      const resultAction = await dispatch(
        updateEmpEmailSettingsThunk({
          employeeId: Empid,
          emailSettings: selectedEmailsPayload,
        })
      );

      if (updateEmpEmailSettingsThunk.fulfilled.match(resultAction)) {
        const updatedEmails = resultAction.payload.response?.[0]?.emailSettings || [];

        dispatch(setSelectedMails(updatedEmails));
        dispatch(setSelectedMailsData(updatedEmails.map((e) => e.email)));
        dispatch(setCheckedMailsData(updatedEmails.map((e) => e.email)));

        if (loginUser?.id === Empid) {
          dispatch(setLoginUserDetails({ ...loginUser, emailSettings: updatedEmails }));
        }

        const newChecked = {};
        updatedEmails.forEach((item) => (newChecked[item.email] = item.status));
        setCheckedEmails(newChecked);

        message.success(resultAction.payload.message || "Email settings updated successfully.");
      } else {
        message.error(resultAction.payload?.message || "Failed to update email settings.");
      }
    } catch (error) {
      console.error("handleSubmitEmails error:", error);
      message.error("Unexpected error occurred.");
    }
  };

  // --------------------- SHARED EMAIL LOGIC ---------------------
  useEffect(() => {
    if (sharedemails?.length) {
      const checks = {};
      sharedemails.forEach((emailObj) => {
        const isChecked = usersharedemails?.some(
          (userEmailObj) =>
            userEmailObj.email === emailObj._id &&
            (userEmailObj.status === true || userEmailObj.status === "true")
        );
        checks[emailObj._id] = isChecked;
      });
      setCheckedSharedEmails(checks);
    }
  }, [sharedemails, usersharedemails]);

  const toggleSharedCheckbox = (id) => {
    setCheckedSharedEmails((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSubmitSharedEmails = async () => {
    const originalStatusMap = {};
    usersharedemails?.forEach((emailObj) => {
      originalStatusMap[emailObj.email] =
        emailObj.status === true || emailObj.status === "true";
    });

    const changedEmailsPayload = sharedemails
      .map((emailObj) => {
        const currentStatus = Boolean(checkedSharedEmails[emailObj._id]);
        const originalStatus = originalStatusMap[emailObj.email] || false;
        return currentStatus !== originalStatus
          ? { email: emailObj._id, status: currentStatus }
          : null;
      })
      .filter(Boolean);

    if (changedEmailsPayload.length === 0) return message.info("No changes to update.");

    try {
      const res = await dispatch(
        updateEmpSharedEmailSettings({
          employeeId: Empid,
          sharedemailSettings: changedEmailsPayload,
        })
      );

      const result = res?.payload;

      if (result?.success) {
        const updatedEmails = result.response?.[0]?.sharedemailSettings || [];

        if (loginUser?.id === Empid) {
          dispatch(setLoginUserDetails({ ...loginUser, sharedemailSettings: updatedEmails }));
        }

        const newChecked = {};
        updatedEmails.forEach((item) => {
          const match = sharedemails.find((e) => e._id === item.email);
          if (match) newChecked[match._id] = item.status;
        });
        setCheckedSharedEmails(newChecked);

        message.success(result.message || "Shared email settings updated.");
      } else {
        message.error(result?.message || "Failed to update shared email settings.");
      }
    } catch (error) {
      console.error("handleSubmitSharedEmails error:", error);
      message.error("Unexpected error occurred.");
    }
  };

  return (
    <div style={{ padding: "20px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      
      {/* PRIVATE EMAILS SECTION */}
      <Form onFinish={handleSubmitEmails}>
        <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", marginBottom: 25 }}>
          <h3 style={{ marginBottom: "20px", fontSize: "20px" }}>Email List</h3>

          {emails?.length === 0 ? (
            <p>No emails found.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {emails
                .filter((emailObj) => emailObj.status === true || emailObj.status === "true")
                .map((emailObj) => (
                  <div key={emailObj._id} style={{ display: "flex", alignItems: "center", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }}>
                    <CheckboxField
                      checked={checkedEmails[emailObj.email] || false}
                      onChange={() => toggleEmailCheckbox(emailObj.email)}
                      label={emailObj.email}
                    />
                  </div>
                ))}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <AntdButton type="primary" label="Submit" htmlType="submit" />
          </div>
        </div>
      </Form>

      {/* SHARED EMAILS SECTION */}
      <Form onFinish={handleSubmitSharedEmails}>
        <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}>
          <h3 style={{ marginBottom: "20px", fontSize: "20px" }}>Shared Email List</h3>

          {loading ? (
            <p>Loading...</p>
          ) : error ? (
            <p>Error loading emails</p>
          ) : sharedemails?.length === 0 ? (
            <p>No shared emails available</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {sharedemails.map((emailObj) => (
                <div key={emailObj._id} style={{ display: "flex", alignItems: "center", padding: "10px", border: "1px solid #ddd", borderRadius: "5px" }}>
                  <CheckboxField
                    checked={checkedSharedEmails[emailObj._id] || false}
                    onChange={() => toggleSharedCheckbox(emailObj._id)}
                    label={emailObj.email}
                  />
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <AntdButton type="primary" label="Submit" htmlType="submit" />
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EmployeeEmailSettings;
